/**
 * Candle Chart - Canvas2D OHLC renderer (miniChart pattern, extended)
 * Korean convention: up = red, down = blue.
 */
import type { Candle } from '../game/state.js';
import { formatPrice } from '../game/utils.js';

export const CANDLE_UP_COLOR = '#FF3D5A';
export const CANDLE_DOWN_COLOR = '#3D7BFF';

interface ChartArea {
  x: number;
  y: number;
  w: number;
  h: number;
  min: number;
  max: number;
}

function priceToY(price: number, area: ChartArea): number {
  const range = area.max - area.min || 1;
  return area.y + area.h - ((price - area.min) / range) * area.h;
}

function drawGrid(ctx: CanvasRenderingContext2D, area: ChartArea): void {
  ctx.strokeStyle = 'rgba(138, 156, 184, 0.12)';
  ctx.lineWidth = 1;
  const lines = 4;
  for (let i = 0; i <= lines; i++) {
    const y = area.y + (area.h / lines) * i;
    ctx.beginPath();
    ctx.moveTo(area.x, y);
    ctx.lineTo(area.x + area.w, y);
    ctx.stroke();
  }
}

function drawCandle(ctx: CanvasRenderingContext2D, candle: Candle, cx: number, bodyW: number, area: ChartArea): void {
  const up = candle.c >= candle.o;
  const color = up ? CANDLE_UP_COLOR : CANDLE_DOWN_COLOR;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;

  // Wick
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx, priceToY(candle.h, area));
  ctx.lineTo(cx, priceToY(candle.l, area));
  ctx.stroke();

  // Body (min 1px height so dojis stay visible)
  const yOpen = priceToY(candle.o, area);
  const yClose = priceToY(candle.c, area);
  const top = Math.min(yOpen, yClose);
  const height = Math.max(1, Math.abs(yOpen - yClose));
  ctx.fillRect(cx - bodyW / 2, top, bodyW, height);
}

function drawPriceLabels(ctx: CanvasRenderingContext2D, area: ChartArea, lastClose: number, lastUp: boolean): void {
  ctx.font = '600 10px Inter, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillStyle = 'rgba(138, 156, 184, 0.7)';
  ctx.fillText(formatPrice(area.max), area.x + 2, area.y + 10);
  ctx.fillText(formatPrice(area.min), area.x + 2, area.y + area.h - 3);

  ctx.textAlign = 'right';
  ctx.fillStyle = lastUp ? CANDLE_UP_COLOR : CANDLE_DOWN_COLOR;
  ctx.fillText(formatPrice(lastClose), area.x + area.w - 2, area.y + 10);
}

/**
 * Render candles onto a canvas (handles devicePixelRatio scaling).
 */
export function drawCandleChart(canvas: HTMLCanvasElement, candles: Candle[]): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const displayW = canvas.clientWidth || canvas.width;
  const displayH = canvas.clientHeight || canvas.height;
  if (canvas.width !== displayW * dpr || canvas.height !== displayH * dpr) {
    canvas.width = displayW * dpr;
    canvas.height = displayH * dpr;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, displayW, displayH);
  if (candles.length === 0) return;

  let min = Infinity;
  let max = -Infinity;
  for (const c of candles) {
    if (c.l < min) min = c.l;
    if (c.h > max) max = c.h;
  }
  // Pad range slightly so candles do not touch the edges
  const pad = (max - min || max * 0.01 || 1) * 0.08;
  const area: ChartArea = { x: 0, y: 4, w: displayW, h: displayH - 8, min: min - pad, max: max + pad };

  drawGrid(ctx, area);

  const slotW = area.w / Math.max(candles.length, 30);
  const bodyW = Math.max(2, slotW * 0.6);
  for (let i = 0; i < candles.length; i++) {
    const cx = area.x + slotW * i + slotW / 2;
    drawCandle(ctx, candles[i], cx, bodyW, area);
  }

  const last = candles[candles.length - 1];
  drawPriceLabels(ctx, area, last.c, last.c >= last.o);
}
